import _ from 'lodash';
import { computed, observable, makeObservable } from 'mobx';
import striptags from 'striptags';
import {DomainObject} from 'shared/store';
import EmployeeTrainingStatusSummary from './EmployeeTrainingStatusSummary';
import {AttachedDocument} from 'main/stores/documents';
import Question from './Question';
import {calendarDate} from 'shared/tools';
import {t} from 'shared/core';

class TrainingProgram extends DomainObject {
  id;
  name = '';
  description;
  lexicalState;
  availableInHiring = true;
  relativeDueDateUnit = null;
  relativeDueDateValue = null;
  reassignmentIntervalUnit = null;
  reassignmentIntervalValue = null;
  absoluteDueDate = null;
  employeeTrainingStatuses;
  launched;
  launchedAt;
  closed;

  attachedFiles = [];
  questions = [];
  videoUrl = '';

  _relations = {
    employeeTrainingStatuses: [EmployeeTrainingStatusSummary],
    attachedFiles: [AttachedDocument],
    questions: [Question],
    _dates: ['launchedAt']
  };

  constructor(other) {
    super();

    makeObservable(this, {
      id: observable,
      name: observable,
      description: observable,
      lexicalState: observable,
      availableInHiring: observable,
      relativeDueDateUnit: observable,
      relativeDueDateValue: observable,
      reassignmentIntervalUnit: observable,
      reassignmentIntervalValue: observable,
      absoluteDueDate: observable,
      employeeTrainingStatuses: observable,
      launched: observable,
      launchedAt: observable,
      closed: observable,
      attachedFiles: observable,
      questions: observable,
      videoUrl: observable,
      dueDateValue: computed,
      launchedAtView: computed,
      truncatedDescription: computed,
      formattedAvailableInHiring: computed,
      formattedDueDate: computed,
      formattedReassignmentInterval: computed,
      sortedQuestions: computed
    });

    this.merge(other);
  }

  get dueDateValue() {
    return this.relativeDueDateValue || '';
  }

  get launchedAtView() {
    return calendarDate(this.launchedAt);
  }

  get truncatedDescription() {
    const strippedDescription = striptags(this.description);
    return _.chain(strippedDescription)
      .trim('&nbsp;')
      .truncate({length: 120})
      .value();
  }

  get formattedAvailableInHiring() {
    switch (this.availableInHiring) {
      case true: return 'Yes';
      case false: return 'No';
      default: throw new Error('Invalid available in hiring type');
    }
  }

  get formattedDueDate() {
    switch (this.relativeDueDateUnit) {
      case 'weeks': return t('training.edit.weeks', { amount: this.relativeDueDateValue });
      case 'days': return t('training.edit.days', { amount: this.relativeDueDateValue });
      case null: return null;
      default: throw new Error('Invalid due date unit');
    }
  }

  get formattedReassignmentInterval() {
    switch (this.reassignmentIntervalUnit) {
      case 'months': return t('training.edit.months', { amount: this.reassignmentIntervalValue });
      case 'years': return t('training.edit.years', { amount: this.reassignmentIntervalValue });
      default: return null;
    }
  }

  get sortedQuestions() {
    return _.sortBy(this.questions, 'order');
  }
}

export default TrainingProgram;
